import { useCallback, useEffect, useRef, useState } from "react";
import FloatLabel from "./FloatLabel";
import {
  Button,
  Card,
  Popover,
  Modal,
  Row,
  Col,
  Input,
  Radio,
  Typography,
  message,
  Popconfirm,
  Spin,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  DownOutlined,
  CloseOutlined,
  SaveOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { URL } from "../helper/Mainurl";

// import moment from "moment";
import "./Notes.css";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { apiClient } from "../utils/apiClient";
const { TextArea } = Input;
// Import FloatLabel component
const Notes = () => {
  const token = localStorage.getItem("authToken"); // Get token from localStorage

  const { Title, Paragraph } = Typography;
  const [layout, setLayout] = useState("grid"); // Default layout is grid
  const [sortOrder, setSortOrder] = useState("newest");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNote, setSelectedNote] = useState(null);
  const [tempSortOrder, setTempSortOrder] = useState(sortOrder);
  const [tempLayout, setTempLayout] = useState(layout); // No pre-selected layout in filter
  const [editedTitle, setEditedTitle] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // Popover open states
  const [sortOpen, setSortOpen] = useState(false);
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [newNoteContent, setNewNoteContent] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const observer = useRef();

  const [notes, setNotes] = useState([
    // { id: 1, title: "Note 1", content: "Re-arrange dentist appointment", date: moment().subtract(2, "days") },
    // { id: 2, title: "Note 2", content: "Re-arrange dentist appointment", date: moment().subtract(1, "days") },
  ]);

  const getData = async (searchQuery = "", sortOrder = " ", pageNum = 1) => {
    if (notesLoading) return;
    try {
      setLoading(true);
      setNotesLoading(true);
      const response = await apiClient.get(`/v1/note/list/${pageNum}`, {
        search: searchQuery,
        sort: sortOrder,
      });

      const data = response.data;

      setLoading(false);
      setNotesLoading(false);

      setNotes((prev) =>
        pageNum === 1 ? data?.data?.notes : [...prev, ...data?.data?.notes]
      ); // Assuming API returns data in this format
      setTotalPages(data?.data?.total_pages);
      setHasMore(pageNum < data?.data?.total_pages);
    } catch (error) {
      setLoading(false);
      setNotesLoading(false);

      console.error("Error fetching notes:", error);
    }
  };

  const lastNoteRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setCurrentPage((prev) => prev + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [notesLoading, hasMore]
  );

  useEffect(() => {
    if (searchQuery.trim() !== "") {
      const delaySearch = setTimeout(() => {
        setCurrentPage(1);
        getData(searchQuery, sortOrder);
      }, 500); // Debounce for better performance

      return () => clearTimeout(delaySearch);
    } else {
      setCurrentPage(1);
      getData("", sortOrder, currentPage); // Fetch data normally when search is empty
    }
  }, [searchQuery, sortOrder, currentPage]);

  const addNote = async () => {
    // setAddLoading(true);
    if (!newNoteTitle.trim()) {
      message.error("Title is required.");
      return;
    }

    if (newNoteTitle.trim().length > 70) {
      message.error("Title should be less than 70 characters.");
      return;
    }

    if (!newNoteContent.trim()) {
      message.error("Content is required.");
      return;
    }

    if (newNoteContent.trim().length > 500) {
      message.error("Title should be less than 100 characters.");
      return;
    }
    const newNote = {
      title: newNoteTitle.trim(),
      description: newNoteContent.trim(),
    };

    try {
      setAddLoading(true);
      const response = await fetch(`${URL}/v1/note/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`, // Add token if required
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newNote),
      });

      if (!response.ok) {
        setAddLoading(false);
        throw new Error("Failed to add note");
      }

      const data = await response.json();
      // Update the UI with the newly added note
      message.success("Note Created Successfully..!");
      getData(searchQuery, sortOrder);
      setAddModalOpen(false);
      setAddLoading(false);
    } catch (error) {
      setAddLoading(false);
      console.error("Error adding note:", error);
    }
  };

  const deleteNote = async (id) => {
    if (!id) {
      message.error("No note selected for deletion.");
      return;
    }

    try {
      const response = await fetch(`${URL}/v1/note/delete/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete note");
      }

      message.success("Note deleted successfully!");
      getData(searchQuery, sortOrder);
      setModalOpen(false);
    } catch (error) {
      console.error("Error deleting note:", error);
      message.error("Error deleting note");
    }
  };

  const openNoteModal = (note) => {
    setSelectedNote(note);
    setEditedTitle(note.title);
    setEditedContent(note.description);
    setModalOpen(true);
    setIsEditing(false);
  };

  const startEditing = () => {
    setIsEditing(true);
  };

  const saveChanges = async (id) => {
    if (!editedTitle.trim()) {
      message.error("Title is required.");
      return;
    }

    if (editedTitle.trim().length > 70) {
      message.error("Title should be less than 70 characters.");
      return;
    }

    if (!editedContent.trim()) {
      message.error("Content is required.");
      return;
    }

    if (editedContent.trim().length > 500) {
      message.error("Title should be less than 100 characters.");
      return;
    }

    const newNote = {
      title: editedTitle || selectedNote.title, // Use existing title if not changed
      description: editedContent || selectedNote.description, // Use existing description if not changed
    };

    try {
      setEditLoading(true);
      const response = await fetch(`${URL}/v1/note/update/${id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`, // Add token if required
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newNote),
      });

      if (!response.ok) {
        setEditLoading(false);
        message.error("Failed to Update note");
      }

      const data = await response.json();

      // Update the UI with the newly added note
      message.success("Note Updated Successfully..!");
      getData(searchQuery, sortOrder);
      setIsEditing(false);
      setModalOpen(false);
      setEditLoading(false);
    } catch (error) {
      setEditLoading(false);
      console.error("Error adding note:", error);
    }

    setIsEditing(false);
  };

  useEffect(() => {
    if (selectedNote) {
      setEditedTitle(selectedNote.title || "");
      setEditedContent(selectedNote.description || "");
    }
  }, [selectedNote]);

  const cancelEditing = () => {
    setEditedTitle(selectedNote.title);
    setEditedContent(selectedNote.description);
    setIsEditing(false);
  };

  useEffect(() => {
    if (sortOpen) {
      // Reset temp state to applied state when opening
      setTempSortOrder(sortOrder);
    }
  }, [sortOpen, sortOrder]);

  useEffect(() => {
    if (layoutOpen) {
      // Reset temp state to applied state when opening
      setTempLayout(layout);
    }
  }, [layoutOpen, layout]);

  const applySortChange = () => {
    setSortOrder(tempSortOrder);
    getData(searchQuery, tempSortOrder); // Fetch data with new sorting order

    setSortOpen(false);
  };

  const applyLayoutChange = () => {
    setLayout(tempLayout); // Keep default to grid if empty
    setLayoutOpen(false);
  };
  const openAddModal = () => {
    setNewNoteTitle("");
    setNewNoteContent("");
    setAddModalOpen(true);
  };
  // Sorting Popover
  const sortContent = (
    <div>
      <Title
        level={3}
        className="NotesFilterTitle"
        style={{ marginTop: "5px", fontSize: "20px", marginBottom: "20px" }}
      >
        Sort
      </Title>
      <Radio.Group
        value={tempSortOrder}
        onChange={(e) => setTempSortOrder(e.target.value)}
      >
        <Radio value="newest">Newest First</Radio>
        <br />
        <Radio value="oldest">Oldest First</Radio>
      </Radio.Group>
      <Button
        className="noteApplyFiter"
        type="primary"
        onClick={applySortChange}
        style={{ marginTop: 10, width: "100%" }}
      >
        Select Sorting
      </Button>
    </div>
  );

  // Layout Popover
  const layoutContent = (
    <div>
      <Title
        level={3}
        className="NotesFilterTitle"
        style={{ marginTop: "5px", fontSize: "20px" }}
      >
        Layout
      </Title>
      <Radio.Group
        value={tempLayout}
        onChange={(e) => setTempLayout(e.target.value)}
      >
        <Radio value="grid">Grid</Radio>
        <br />
        <Radio value="row">Row</Radio>
      </Radio.Group>
      <Button
        className="noteApplyFiter"
        type="primary"
        onClick={applyLayoutChange}
        style={{ marginTop: 10, width: "100%" }}
      >
        Select Layout
      </Button>
    </div>
  );

  const handleDeleteConfirm = () => {
    if (noteToDelete) {
      deleteNote(noteToDelete); // Call your delete API function
    }
    setDeleteModalVisible(false); // Close the modal
    setNoteToDelete(null); // Reset state
  };

  const columnProps =
    layout === "grid" ? { xs: 24, sm: 12, lg: 8, xl: 6 } : { span: 24 };
  const cardMaxHeight = layout === "grid" ? "350px" : "none";
  const cardMarginBottom = layout === "row" ? "16px" : "0px";

  return (
    <div className="notes-page-container">
      <div className="notes-header">
        <h2 style={{ margin: 0 }}>Notes</h2>
        <div className="notes-actions">
          {/* Sort Popover */}
          <Popover
            overlayClassName="notePopover"
            content={sortContent}
            trigger="click"
            open={sortOpen}
            onOpenChange={setSortOpen}
            placement="bottom"
          >
            <Button className="noteFilterBtn">
              {sortOrder === "newest" ? "Newest First" : "Oldest First"}{" "}
              <DownOutlined style={{ color: "#767A83" }} />
            </Button>
          </Popover>

          {/* Layout Popover */}
          <Popover
            overlayClassName="notePopover"
            content={layoutContent}
            className="hide-on-mobile"
            trigger="click"
            open={layoutOpen}
            onOpenChange={setLayoutOpen}
            placement="bottom"
          >
            <Button className="noteFilterBtn">
              {layout === "grid" ? "Grid" : "Row"}{" "}
              <DownOutlined style={{ color: "#767A83" }} />
            </Button>
          </Popover>

          <Button
            className="notes-add-btn"
            type="primary"
            icon={<PlusOutlined />}
            onClick={openAddModal}
          >
            Add Note
          </Button>
        </div>
      </div>

      <Input
        className="notes-search"
        placeholder="Search notes..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{
          width: "100%",
          marginBottom: 30,
          height: "50px",
          marginTop: "20px",
        }}
        prefix={<SearchOutlined />}
      />

      <Row
        gutter={[16, 16]}
        className={`notes-grid ${layout === "row" ? "notes-grid-row" : ""}`}
      >
        {/* {loading ? (
          <Spin></Spin>
        ) : ( */}
        <>
          {notes && notes.length ? (
            notes.map((note, index) => {
              const isLast = index === notes.length - 1;
              return (
                <Col
                  {...columnProps}
                  key={note.id}
                  ref={isLast ? lastNoteRef : null}
                >
                  <Card
                    className="notesCard"
                    style={{
                      cursor: "pointer",
                      maxHeight: cardMaxHeight,
                      marginBottom: cardMarginBottom, // Add margin-bottom when layout is "row"
                    }}
                    onClick={() => openNoteModal(note)}
                  >
                    <div className="card-content-notes">
                      <Title level={4} className="noteCardTitle">
                        {note.title}
                      </Title>
                      <Paragraph>{note.description}</Paragraph>
                    </div>
                    <div
                      className="noteCardBottom"
                      style={{
                        // marginTop: 97,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <small style={{ flex: "1" }}>{note.created_at}</small>
                      <Button
                        className="notesCardDelete"
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevents modal from opening
                          setNoteToDelete(note.id); // Store note ID to delete

                          setDeleteModalVisible(true); // Show confirmation modal
                        }}
                      />
                    </div>
                  </Card>
                </Col>
              );
            })
          ) : (
            <h3 style={{ textAlign: "center" }}>No Data Found</h3>
          )}

          {notesLoading && (
            <div style={{ width: "100%", textAlign: "center", margin: 20 }}>
              <Spin />
            </div>
          )}
        </>
        {/* )} */}
      </Row>
      <Modal
        className="notesModal"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        maskClosable={false}
        width={700}
        footer={[
          isEditing ? (
            <>
              <Button
                key="save"
                type="primary"
                className="noteSaveModalBtn"
                loading={editLoading}
                discabled={editLoading}
                onClick={() => saveChanges(selectedNote.id)}
              >
                Apply
              </Button>
            </>
          ) : (
            <></>
          ),
        ]}
      >
        <div className="noteModalHeader">
          <Title className="notesModelHeader" level={4}>
            {isEditing ? "Edit Note" : "View Note"}
          </Title>
          {isEditing ? (
            <>
              <Button
                className="noteModalDelete"
                key="cancel"
                onClick={cancelEditing}
              >
                Reset
              </Button>
            </>
          ) : (
            <>
              <Button
                key="edit"
                type="primary"
                style={{ fontSize: "14px", marginRight: "15px" }}
                onClick={startEditing}
              >
                Edit Notes <EditOutlined style={{ marginLeft: "15px" }} />
              </Button>
              <Button
                className="noteModalDelete"
                key="delete"
                icon={<DeleteOutlined />}
                onClick={() => deleteNote(selectedNote.id)}
              />
            </>
          )}
        </div>

        {selectedNote && (
          <>
            {isEditing ? (
              <>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 4,
                      color: "#9254de",
                      fontSize: 13,
                    }}
                  >
                    Title
                  </label>
                  <Input
                    placeholder=""
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 4,
                      color: "#9254de",
                      fontSize: 13,
                    }}
                  >
                    Content
                  </label>
                  {/* <TextArea 
                    value={editedContent} onChange={(e) => setEditedContent(e.target.value)}
                    autoSize={{ minRows: 4, maxRows: 10 }} 
                    /> */}
                  <>
                    <TextArea
                      value={editedContent}
                      maxLength={500}
                      placeholder=""
                      autoSize={{ minRows: 4, maxRows: 20 }}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setEditedContent(e.target.value);
                        }
                      }}
                    />
                    <div
                      style={{
                        fontSize: 12,
                        color: "#9254de",
                        textAlign: "right",
                      }}
                    >
                      {500 - editedContent.length} characters remaining
                    </div>
                  </>
                </div>
              </>
            ) : (
              <>
                <Title style={{ fontSize: "20px" }} level={4}>
                  {selectedNote.title}
                </Title>
                <Paragraph>{selectedNote.description}</Paragraph>
              </>
            )}
          </>
        )}
      </Modal>
      <Modal
        title="Add Note"
        open={addModalOpen}
        closable={false}
        onCancel={() => setAddModalOpen(false)}
        maskClosable={false}
        footer={[
          <Popconfirm
            key="cancel"
            title="Do you really want to cancel? Changes not saved."
            okText="Yes"
            cancelText="No"
            onConfirm={() => setAddModalOpen(false)}
          >
            <Button>Cancel</Button>
          </Popconfirm>,
          <Button
            key="add"
            type="primary"
            loading={addLoading}
            disabled={addLoading}
            onClick={addNote}
          >
            Add Note
          </Button>,
        ]}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 12,
          }}
        >
          <Button
            size="small"
            onClick={() => {
              setNewNoteTitle("");
              setNewNoteContent("");
            }}
          >
            Reset
          </Button>
        </div>
        <FloatLabel label="Title" value={newNoteTitle}>
          <Input
            placeholder=""
            value={newNoteTitle}
            onChange={(e) => setNewNoteTitle(e.target.value)}
          />
        </FloatLabel>
        <FloatLabel label="Content" value={newNoteContent}>
          {/* <TextArea autoSize={{ minRows: 10, maxRows: 20 }}  value={newNoteContent} onChange={(e) => setNewNoteContent(e.target.value)} /> */}
          <>
            <TextArea
              value={newNoteContent}
              maxLength={500}
              placeholder=""
              autoSize={{ minRows: 10, maxRows: 20 }}
              onChange={(e) => {
                if (e.target.value.length <= 500) {
                  setNewNoteContent(e.target.value);
                }
              }}
            />
            <div style={{ fontSize: 12, color: "#9254de", textAlign: "right" }}>
              {500 - newNoteContent.length} characters remaining
            </div>
          </>
        </FloatLabel>
      </Modal>

      <div>
        {deleteModalVisible && (
          <DeleteConfirmationModal
            visible={deleteModalVisible}
            onConfirm={handleDeleteConfirm} // Trigger delete on confirm
            onCancel={() => setDeleteModalVisible(false)} // Close modal on cancel
          />
        )}
      </div>
    </div>
  );
};

export default Notes;
